import { lazy, Suspense, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { LoginFormContainer } from "@/features/auth/components/LoginForm";
import { LoginBackdrop } from "@/features/auth/components/LoginLayout/LoginBackdrop";
import { LoginBrandPanel } from "@/features/auth/components/LoginLayout/LoginBrandPanel";
import { LoginFirstVisit } from "@/features/auth/components/LoginLayout/LoginFirstVisit";
import { LoginHeader } from "@/features/auth/components/LoginLayout/LoginHeader";
import { LoginIntro } from "@/features/auth/components/LoginLayout/LoginIntro";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useLoginOnboardingTrail } from "@/features/auth/hooks/useLoginOnboardingTrail";
import { useOrganizationByToken } from "@/features/auth/hooks/useOrganizationByToken";
import { LoginLegalDocuments } from "@/features/legal/components/LoginLegalDocuments";
import { BetaAlert } from "@/shared/components/BetaAlert";
import { AUTH_SHOWCASE_CARD_CLASS, AUTH_SHOWCASE_COLUMN_CLASS } from "@/shared/components/authShowcase/authShowcase.style";
import { cn } from "@/shared/utils/style.util";

// Vite enables this import only for its local development server, never for a build.
const LocalBrowserLogin = import.meta.env.LOCAL_BROWSER_LOGIN
  ? lazy(() => import("@/features/auth/local-browser-login/LocalBrowserLogin"))
  : null;

// The form column keeps the same position in both layouts: the login form
// container must stay mounted from the credentials to the last onboarding
// step, which renders its own showcase card.
const LAYOUT_CLASS = {
  login: { wrapper: "max-w-6xl", grid: AUTH_SHOWCASE_CARD_CLASS, column: AUTH_SHOWCASE_COLUMN_CLASS },
  // The onboarding steps carry more content than the credentials: a wider card keeps them airy.
  onboarding: { wrapper: "max-w-7xl", grid: "", column: "" },
} as const;

const isBetaVersion = (): boolean => (import.meta.env.VITE_BETA_VERSION ?? import.meta.env.BETA_VERSION) === "true";

export const LoginPage: React.FC = () => {
  const {
    user, isAuthenticated, isLegalComplianceRequired, refreshSession, isAuthenticating, isAuthFailed,
  } = useAuth();
  const trail = useLoginOnboardingTrail();
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl ?? "/";

  const [searchParams] = useSearchParams();
  const orgToken = searchParams.get("token");
  const { data: orgBranding, isLoading: isBrandingLoading } = useOrganizationByToken(orgToken);
  // Tant que le branding du CSE se charge, on n'affiche pas le logo Yeati :
  // sinon on voit le logo Yeati « flasher » avant de basculer sur le CSE.
  const isResolvingBranding = Boolean(orgToken) && isBrandingLoading;

  const isBetaEnabled = isBetaVersion();
  const { isOnboardingActive } = trail;
  const layoutClass = isOnboardingActive ? LAYOUT_CLASS.onboarding : LAYOUT_CLASS.login;

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticating && !isAuthFailed && !isLegalComplianceRequired) {
      refreshSession();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, navigate, returnUrl]);

  return (
    <section className="relative isolate flex min-h-screen flex-col bg-gray-50 font-plus-jakarta dark:bg-slate-950">
      <LoginBackdrop />
      <LoginHeader
        orgBranding={orgBranding}
        organizationName={user?.employee.organization?.name ?? null}
        isResolvingBranding={isResolvingBranding}
        isBetaEnabled={isBetaEnabled}
      />
      {/* `my-auto` centres the card vertically below the header. */}
      <div className={cn("mx-auto my-auto w-full px-4 pb-12 sm:px-6", layoutClass.wrapper)}>
        <div className={layoutClass.grid}>
          {!isOnboardingActive && <LoginBrandPanel />}
          <div className={cn("flex w-full flex-col", layoutClass.column)}>
            {!isOnboardingActive && isBetaEnabled && <BetaAlert supportLink="https://mon-yeati.co/connexion" />}
            {!isOnboardingActive && <LoginIntro />}
            <LoginFormContainer trail={trail} />
            {!isOnboardingActive && <LoginFirstVisit />}
            <LoginLegalDocuments isHidden={isOnboardingActive} />
            {LocalBrowserLogin && (
              <Suspense fallback={null}>
                <LocalBrowserLogin />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
